import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Chip,
	CircularProgress,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
	getPersonVoyagerLog,
	VoyagerLogItem,
	VoyagerLogResponse,
} from "../../api/person/voyager-log";
import { formatDateTimeWithMilliseconds } from "../../utils/date-formatting";
import { getVoyagerStatusColor } from "../../utils/voyager-status-colors";
import { useGlobalState } from "../globalState";

interface VoyagerLogHistoryProps {
	id: number;
	loadHistory: (id: number) => Promise<VoyagerLogResponse>;
}

export const VoyagerLogHistory = ({ id, loadHistory }: VoyagerLogHistoryProps) => {
	const [voyagerLog, setVoyagerLog] = useState<VoyagerLogItem[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const { currentUser } = useGlobalState();

	const fetchVoyagerLog = useCallback(async () => {
		if (!currentUser.is_superuser || !id) {
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			const response = await loadHistory(id);
			setVoyagerLog(response.results);
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to load voyager log");
		} finally {
			setLoading(false);
		}
	}, [id, currentUser.is_superuser, loadHistory]);

	useEffect(() => {
		fetchVoyagerLog();
	}, [fetchVoyagerLog]);

	// Don't render anything if user is not superuser
	if (!currentUser.is_superuser) {
		return null;
	}

	if (loading) {
		return (
			<Accordion defaultExpanded={false} data-component="voyager-log-accordion">
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">Voyager Log</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<CircularProgress />
					</Box>
				</AccordionDetails>
			</Accordion>
		);
	}

	if (error) {
		return (
			<Accordion defaultExpanded={false} data-component="voyager-log-accordion">
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					data-component="accordion-summary"
				>
					<Typography variant="h6">Voyager Log</Typography>
				</AccordionSummary>
				<AccordionDetails data-component="accordion-details">
					<Typography color="error">Error: {error}</Typography>
				</AccordionDetails>
			</Accordion>
		);
	}

	return (
		<Accordion defaultExpanded={false} data-component="voyager-log-accordion">
			<AccordionSummary
				expandIcon={<ExpandMoreIcon />}
				data-component="accordion-summary"
			>
				<Typography variant="h6">Voyager Log ({voyagerLog.length})</Typography>
			</AccordionSummary>
			<AccordionDetails data-component="accordion-details">
				{voyagerLog.length === 0 ? (
					<Typography color="text.secondary">
						No voyager log entries found for this profile.
					</Typography>
				) : (
					<TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
						<Table size="small" sx={{ minWidth: 720 }}>
							<TableHead>
								<TableRow>
									<TableCell>ID</TableCell>
									<TableCell>Date</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Resource</TableCell>
									<TableCell>Caller</TableCell>
									<TableCell>Bot</TableCell>
									<TableCell>Proxy</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{voyagerLog.map((item) => (
									<TableRow key={item.id}>
										<TableCell>
											<Typography variant="body2" noWrap>
												{item.id}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography variant="body2" noWrap>
												{formatDateTimeWithMilliseconds(
													item.created
												)}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip
												label={item.status_code || "N/A"}
												color={getVoyagerStatusColor(
													item.status_code
												)}
												size="small"
											/>
										</TableCell>
										<TableCell>
											<Tooltip
												title={item.url}
												placement="top"
												arrow
											>
												<Typography
													variant="body2"
													noWrap
													sx={{ cursor: "help" }}
												>
													{item.resource_label || ""}
												</Typography>
											</Tooltip>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												noWrap
												title={item.caller_type_label}
											>
												{item.caller_type_label || ""}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												noWrap
												title={item.bot_name}
											>
												{item.bot_name}
											</Typography>
										</TableCell>
										<TableCell>
											<Typography
												variant="body2"
												noWrap
												title={item.proxy_uri}
											>
												{item.proxy_id ?? "N/A"}
											</Typography>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				)}
			</AccordionDetails>
		</Accordion>
	);
};

interface PersonVoyagerLogHistoryProps {
	personId: number;
}

export const PersonVoyagerLogHistory = ({ personId }: PersonVoyagerLogHistoryProps) => {
	return <VoyagerLogHistory id={personId} loadHistory={getPersonVoyagerLog} />;
};
